import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8a4b748d-a67a-4760-b635-c7f9b7921d7e/artifacts/kn8679bx_Feral%20Ways%20Logo.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Mapping', path: '/mapping' },
  { name: 'Projects', path: '/projects' },
  { name: 'About', path: '/about' },
  { name: 'Store', path: '/store' },
  { name: 'Contact', path: '/contact' },
  { name: 'Client Login', path: '/client/login', icon: true },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const isHomePage = location.pathname === '/';

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-5xl transition-all duration-500 ${
          isScrolled || !isHomePage
            ? 'navbar-glass shadow-lg border border-white/30'
            : 'bg-white/20 backdrop-blur-md border border-white/10'
        } rounded-full px-4 md:px-8 py-3`}
      >
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="F.E.R.A.Lways" 
              className="h-10 w-10 md:h-12 md:w-12 object-contain"
            />
            <span className={`font-serif text-lg md:text-xl font-semibold hidden sm:block ${
              isScrolled || !isHomePage ? 'text-forest' : 'text-white'
            }`}>
              F.E.R.A.Lways
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-forest flex items-center gap-1.5 ${
                  location.pathname === link.path
                    ? 'text-forest'
                    : isScrolled || !isHomePage
                    ? 'text-foreground/80'
                    : 'text-white/90'
                }`}
              >
                {link.icon && <Lock className="w-3.5 h-3.5" />}
                {link.name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-full hover:bg-forest/10 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <X className={`w-6 h-6 ${isScrolled || !isHomePage ? 'text-forest' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled || !isHomePage ? 'text-forest' : 'text-white'}`} />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed top-24 left-1/2 -translate-x-1/2 z-40 w-[90%] max-w-md navbar-glass rounded-2xl shadow-xl border border-white/30 p-6 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  className={`text-lg font-medium py-2 px-4 rounded-xl transition-colors flex items-center gap-2 ${
                    location.pathname === link.path
                      ? 'bg-forest text-white'
                      : 'text-foreground hover:bg-forest/10'
                  }`}
                >
                  {link.icon && <Lock className="w-4 h-4" />}
                  {link.name}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
